/**
 * A fotografia escolhida de um grupo, na pré-análise (E123.F3, GUS-128, ADR-0079 E.24).
 *
 * Mostra a imagem, de qual documento e página ela saiu e quem a escolheu — o sistema ou o perito.
 * A origem fica ao lado da imagem, e não escondida: a conferência compara aquele rosto porque ele
 * veio daquele documento, e o perito revisa a escolha olhando as duas coisas juntas.
 */
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CandidatasDoCasoData } from '@/lib/candidatas_de_imagem';
import {
  motivoDaPendencia,
  motivoSemVinculoDaFoto,
  origemDaFoto,
  recortePendente,
} from '@/lib/fotografias_do_caso';
import type { FotoDoSignatarioData, GrupoDaFoto } from '@/lib/fotos_do_signatario';

import { FotoDoSignatarioThumb } from './FotoDoSignatarioThumb';

const t = ptBR.exameEletronico.fotografias;

const TOM_DO_AVISO = 'border border-warning bg-warning-50 px-sc-3 py-sc-2 text-warning-700';

export interface FotoEleitaCartaoProps {
  caseId: string;
  grupo: GrupoDaFoto;
  foto: FotoDoSignatarioData | null;
  candidatas: CandidatasDoCasoData;
  /** E141.F2: o perito concluiu alguma peça — o recorte não muda mais. */
  concluido?: boolean;
  /** E141.F2: abre o ajuste do recorte da foto escolhida; ausente onde a seção não o oferece. */
  onAjustar?: (foto: FotoDoSignatarioData) => void;
}

/**
 * E141.F2 (ADR-0079 E.26): a leitura automática não achou a fotografia (ou falhou) e a imagem entrou
 * inteira na conferência — o cartão aponta a pendência com o motivo, ao lado da origem.
 */
function PendenciaDoRecorte({
  grupo,
  foto,
}: {
  grupo: GrupoDaFoto;
  foto: FotoDoSignatarioData;
}): JSX.Element | null {
  const motivo = motivoDaPendencia(foto);
  if (motivo === null) return null;
  const titulo =
    motivo === 'falha_da_leitura' ? t.recortePendente.tituloFalha : t.recortePendente.titulo;
  return (
    <div
      role="status"
      className={TOM_DO_AVISO}
      data-testid={`fotografia-eleita-${grupo}-recorte-pendente`}
      data-motivo={motivo}
    >
      <p className="text-body-sm font-semibold">{titulo}</p>
      <p className="mt-1 max-w-prose text-body-sm">
        {t.recortePendente.texto.replace('{motivo}', t.motivoDaPendencia[motivo])}
      </p>
    </div>
  );
}

/**
 * E162.F1.T08 (ADR-0111 §2.4, QA-25): a fotografia saiu de um documento sem vínculo demonstrado
 * com o contrato e a pessoa do caso — a conferência fica registrada, mas não corrobora a autoria.
 */
function AvisoDoVinculo({
  grupo,
  foto,
  candidatas,
}: {
  grupo: GrupoDaFoto;
  foto: FotoDoSignatarioData;
  candidatas: CandidatasDoCasoData;
}): JSX.Element | null {
  const motivo = motivoSemVinculoDaFoto(foto, candidatas);
  if (motivo === null) return null;
  return (
    <div
      role="status"
      className={TOM_DO_AVISO}
      data-testid={`fotografia-eleita-${grupo}-sem-vinculo`}
      data-motivo={motivo}
    >
      <p className="text-body-sm font-semibold">{t.semVinculo.titulo}</p>
      <p className="mt-1 max-w-prose text-body-sm">
        {t.semVinculo.texto.replace('{motivo}', t.semVinculo.motivo[motivo])}
      </p>
    </div>
  );
}

function textoDaOrigem(foto: FotoDoSignatarioData, candidatas: CandidatasDoCasoData): string {
  const origem = origemDaFoto(foto, candidatas);
  if (origem === null) return foto.nome_arquivo;
  return origem.pagina === null
    ? origem.documento
    : t.origemComPagina(origem.documento, origem.pagina);
}

export function FotoEleitaCartao({
  caseId,
  grupo,
  foto,
  candidatas,
  concluido = false,
  onAjustar,
}: FotoEleitaCartaoProps): JSX.Element {
  return (
    <div
      className="flex gap-sc-3 border border-border p-sc-3"
      data-testid={`fotografia-eleita-${grupo}`}
    >
      {foto !== null ? <FotoDoSignatarioThumb caseId={caseId} foto={foto} /> : null}
      <div className="min-w-0 flex-1 space-y-sc-1">
        <p className="text-body-sm font-semibold text-fg">{t.grupos[grupo]}</p>
        {foto === null ? (
          <p className="text-body-sm text-fg-muted">{t.semFotoNoGrupo}</p>
        ) : (
          <>
            <p className="truncate text-body-sm text-fg" title={textoDaOrigem(foto, candidatas)}>
              <span className="text-fg-muted">{t.origem}: </span>
              <span data-testid={`fotografia-eleita-${grupo}-origem`}>
                {textoDaOrigem(foto, candidatas)}
              </span>
            </p>
            <p className="text-caption text-fg-dim" data-testid={`fotografia-eleita-${grupo}-quem`}>
              {foto.confirmacao_automatica === true
                ? t.escolhidaPeloSistema
                : t.escolhidaPeloPerito}
            </p>
            <AvisoDoVinculo grupo={grupo} foto={foto} candidatas={candidatas} />
            {recortePendente(foto) ? <PendenciaDoRecorte grupo={grupo} foto={foto} /> : null}
            {onAjustar !== undefined ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={concluido}
                onClick={() => onAjustar(foto)}
                data-testid={`fotografia-eleita-${grupo}-ajustar`}
              >
                {t.ajustarRecorte}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
